/**
 * Phone attitude and shake from the device sensors (DeviceOrientation /
 * DeviceMotion). iOS requires an explicit permission request from inside a
 * user gesture; call `requestPermission()` from a tap handler.
 *
 * `beta` is front/back tilt: ~0° lying flat face up, ~90° held upright,
 * ±180° face down. `gamma` is left/right roll. Both are smoothed here.
 */
export type Attitude = 'flat' | 'upright' | 'between';

export class DeviceMotionInput {
  /** smoothed tilt in degrees */
  beta = 90;
  gamma = 0;
  /** true once orientation events have actually arrived (desktops never send any) */
  active = false;
  onShake?: (strength: number) => void;

  private lastShake = 0;
  private granted: boolean | null = null;
  private lastMag = 9.8;

  constructor() {
    window.addEventListener('deviceorientation', (e) => {
      if (e.beta === null) return;
      this.active = true;
      // ease with a fixed-rate exponential; events arrive ~60 Hz
      this.beta += (e.beta - this.beta) * 0.12;
      this.gamma += ((e.gamma ?? 0) - this.gamma) * 0.12;
    });
    window.addEventListener('devicemotion', (e) => {
      const a = e.accelerationIncludingGravity;
      if (!a || a.x === null) return;
      const mag = Math.hypot(a.x, a.y ?? 0, a.z ?? 0);
      const jerk = Math.abs(mag - this.lastMag);
      this.lastMag = mag;
      const now = performance.now();
      if (jerk > 12 && now - this.lastShake > 1500) {
        this.lastShake = now;
        this.onShake?.(Math.min(1, (jerk - 12) / 15));
      }
    });
  }

  /** iOS 13+ needs this from a user gesture; elsewhere it's a no-op. */
  async requestPermission(): Promise<boolean> {
    if (this.granted !== null) return this.granted;
    const DOE = DeviceOrientationEvent as unknown as { requestPermission?: () => Promise<'granted' | 'denied'> };
    const DME = DeviceMotionEvent as unknown as { requestPermission?: () => Promise<'granted' | 'denied'> };
    try {
      const o = DOE.requestPermission ? await DOE.requestPermission() : 'granted';
      const m = DME.requestPermission ? await DME.requestPermission() : 'granted';
      this.granted = o === 'granted' && m === 'granted';
    } catch {
      this.granted = false;
    }
    return this.granted;
  }

  get attitude(): Attitude {
    const b = Math.abs(this.beta);
    if (b < 25 || b > 155) return 'flat';
    if (b > 55 && b < 125) return 'upright';
    return 'between';
  }
}
